import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { FlightMap } from '../components/map/FlightMap';
import { missionsApi, dronesApi, pdfApi } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import NotamPanel from '../components/NotamPanel';
import WeatherPanel from '../components/weather/WeatherPanel';
import { polygonCentroid } from '../components/weather/polygonCentroid';
import { 
  ArrowLeft, 
  Download, 
  Printer,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Shield,
  MapPin,
  Calendar,
  Clock,
  Plane,
  Gauge,
  FileText,
  Phone,
  ExternalLink,
  Building2,
  Radio,
  AlertCircle,
  Timer,
  ChevronRight,
  Navigation
} from 'lucide-react';

// Composant pour afficher les héliports/aéroports proches
const NearbyAirportsCard = ({ nearbyAirportsInfo }) => {
  if (!nearbyAirportsInfo || nearbyAirportsInfo.total_count === 0) return null;
  
  const { heliports, regular_airports, hospitals } = nearbyAirportsInfo;
  const allAirports = [...(hospitals || []), ...(heliports || []), ...(regular_airports || [])].slice(0, 5);
  
  if (allAirports.length === 0) return null;
  
  const getTypeIcon = (type) => {
    if (type === 'HOSPITAL_HELIPAD') return '🏥';
    if (type?.includes('HELIPORT') || type?.includes('HELIPAD')) return '🚁';
    if (type === 'INTL_APT') return '🛫';
    return '✈️';
  };
  
  const getTypeColor = (type, distance) => {
    if (type === 'HOSPITAL_HELIPAD') return 'bg-red-100 border-red-300 text-red-800';
    if (distance < 1000) return 'bg-amber-100 border-amber-300 text-amber-800';
    if (type?.includes('HELIPORT')) return 'bg-sky-100 border-sky-300 text-sky-800';
    return 'bg-slate-100 border-slate-300 text-slate-700';
  };
  
  return (
    <div className="bg-gradient-to-br from-sky-50 to-blue-50 border-2 border-sky-200 rounded-xl p-5 shadow">
      <h3 className="font-bold text-sky-900 flex items-center gap-2 mb-4">
        <Navigation className="w-5 h-5 text-sky-600" />
        Aéroports & Héliports Proches
        <span className="ml-auto px-2 py-0.5 text-xs font-medium bg-sky-200 text-sky-800 rounded-full">
          {nearbyAirportsInfo.total_count} détecté{nearbyAirportsInfo.total_count > 1 ? 's' : ''}
        </span>
      </h3>
      
      <div className="space-y-2">
        {allAirports.map((airport, idx) => (
          <div 
            key={airport.id || idx}
            className={`flex items-center justify-between p-3 rounded-lg border ${getTypeColor(airport.type, airport.distance_m)}`}
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">{getTypeIcon(airport.type)}</span>
              <div>
                <p className="font-semibold text-sm">{airport.name}</p>
                <p className="text-xs opacity-75">{airport.type_label}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-bold text-sm">{airport.distance_km} km</p>
              {airport.icao && <p className="text-xs font-mono opacity-75">{airport.icao}</p>}
            </div>
          </div>
        ))}
      </div>
      
      {nearbyAirportsInfo.heliport_count > 0 && (
        <p className="mt-3 text-xs text-sky-700 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          {nearbyAirportsInfo.hospital_count > 0 
            ? `Dont ${nearbyAirportsInfo.hospital_count} hélistation(s) d'hôpital - Priorité haute`
            : 'Coordination recommandée avec les héliports proches'
          }
        </p>
      )}
    </div>
  );
};

// Composant pour afficher une contrainte avec son action
const ConstraintCard = ({ constraint, missionDate }) => {
  // Calculer les jours restants
  const calculateDaysRemaining = () => {
    if (!missionDate || constraint.delay_days <= 0) return null;
    const mission = new Date(missionDate);
    const today = new Date();
    const diffTime = mission - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  
  const daysRemaining = calculateDaysRemaining();
  const isUrgent = daysRemaining !== null && daysRemaining <= constraint.delay_days;
  const isOverdue = daysRemaining !== null && daysRemaining < 0;
  
  const priorityColors = {
    critical: 'bg-red-50 border-red-300',
    high: 'bg-amber-50 border-amber-300',
    medium: 'bg-sky-50 border-sky-300',
    low: 'bg-slate-50 border-slate-300'
  };
  
  const actionColors = {
    declaration: 'bg-emerald-500 hover:bg-emerald-600',
    agreement: 'bg-amber-500 hover:bg-amber-600',
    authorization: 'bg-red-500 hover:bg-red-600',
    contact: 'bg-sky-500 hover:bg-sky-600',
    check: 'bg-slate-500 hover:bg-slate-600',
    forbidden: 'bg-red-800 cursor-not-allowed'
  };
  
  return (
    <div className={`rounded-lg border-2 p-4 ${priorityColors[constraint.priority] || priorityColors.medium}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">{constraint.icon}</span>
            <h4 className="font-semibold text-slate-900">{constraint.title}</h4>
            {constraint.cerfa && (
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">CERFA</span>
            )}
          </div>
          <p className="text-sm text-slate-600 mb-3">{constraint.description}</p>
          
          {/* Délai avec jours restants */}
          {constraint.delay_days > 0 && (
            <div className="flex items-center gap-2 mb-3">
              <Timer className="w-4 h-4 text-slate-500" />
              <span className="text-sm text-slate-600">{constraint.delay_label}</span>
              {daysRemaining !== null && (
                <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                  isOverdue ? 'bg-red-500 text-white' :
                  isUrgent ? 'bg-amber-500 text-white' :
                  'bg-emerald-100 text-emerald-700'
                }`}>
                  {isOverdue ? `${Math.abs(daysRemaining)}j en retard` : `${daysRemaining}j restants`}
                </span>
              )}
            </div>
          )}
          
          {/* Fréquence si disponible */}
          {constraint.frequency && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Radio className="w-3 h-3" />
              <span>{constraint.service_name}: {constraint.frequency}</span>
            </div>
          )}
        </div>
        
        {/* Bouton d'action */}
        <div className="flex-shrink-0">
          {constraint.action_type !== 'forbidden' ? (
            constraint.action_url && constraint.action_url.trim() !== '' ? (
              <a
                href={constraint.action_url}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors ${actionColors[constraint.action_type] || actionColors.check}`}
                onClick={(e) => {
                  // S'assurer que le lien s'ouvre bien
                  if (!constraint.action_url || constraint.action_url === '#') {
                    e.preventDefault();
                    alert('Lien non disponible. Consultez le SIA pour les contacts.');
                  }
                }}
              >
                {constraint.action_label}
                <ExternalLink className="w-4 h-4" />
              </a>
            ) : (
              <button
                onClick={() => window.open('https://www.sia.aviation-civile.gouv.fr/', '_blank')}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors ${actionColors[constraint.action_type] || actionColors.check}`}
              >
                {constraint.action_label}
                <ExternalLink className="w-4 h-4" />
              </button>
            )
          ) : (
            <span className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg ${actionColors.forbidden}`}>
              {constraint.action_label}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Fonction pour calculer la superficie d'un polygone en hectares (formule de Shoelace)
const calculatePolygonArea = (geometry) => {
  if (!geometry) return null;
  
  // Extraire les coordonnées selon le format (GeoJSON ou array)
  let coords;
  if (geometry.coordinates && geometry.coordinates[0]) {
    coords = geometry.coordinates[0]; // Format GeoJSON [[[lng, lat], ...]]
  } else if (Array.isArray(geometry) && geometry[0]) {
    // Format array [[lat, lng], ...]
    coords = geometry.map(c => [c[1], c[0]]); // Convertir en [lng, lat]
  } else {
    return null;
  }
  
  if (coords.length < 3) return null;
  
  // Calculer le centroïde pour la projection
  const centerLat = coords.reduce((sum, c) => sum + c[1], 0) / coords.length;
  
  // Facteurs de conversion en mètres (approximation)
  const latToMeters = 111320; // mètres par degré de latitude
  const lngToMeters = 111320 * Math.cos(centerLat * Math.PI / 180); // mètres par degré de longitude
  
  // Convertir en coordonnées métriques
  const metersCoords = coords.map(c => [
    c[0] * lngToMeters,
    c[1] * latToMeters
  ]);
  
  // Formule de Shoelace pour calculer l'aire
  let area = 0;
  const n = metersCoords.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += metersCoords[i][0] * metersCoords[j][1];
    area -= metersCoords[j][0] * metersCoords[i][1];
  }
  area = Math.abs(area) / 2;
  
  // Convertir en hectares (1 hectare = 10 000 m²)
  const hectares = area / 10000;
  
  return hectares;
};

// Formater la superficie pour l'affichage
const formatArea = (hectares) => {
  if (hectares === null || hectares === undefined) return 'N/A';
  
  if (hectares < 0.01) {
    // Moins de 100 m², afficher en m²
    return `${Math.round(hectares * 10000)} m²`;
  } else if (hectares < 1) {
    // Moins d'1 hectare, afficher en m² ou en ares
    const m2 = hectares * 10000;
    if (m2 < 1000) {
      return `${Math.round(m2)} m²`;
    }
    return `${(hectares * 100).toFixed(1)} ares`;
  } else if (hectares < 100) {
    return `${hectares.toFixed(2)} ha`;
  } else {
    return `${hectares.toFixed(1)} ha`;
  }
};

export default function MissionDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mission, setMission] = useState(null);
  const [drone, setDrone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null); // Référence vers l'instance Leaflet

  // Callback pour recevoir l'instance de carte depuis FlightMap
  const handleMapReady = useCallback((mapInstance) => {
    mapInstanceRef.current = mapInstance;
  }, []);

  useEffect(() => {
    loadMission();
  }, [id]);

  const loadMission = async () => {
    try {
      const response = await missionsApi.get(id);
      setMission(response.data);
      
      // Load drone info
      if (response.data.drone_id) {
        const droneRes = await dronesApi.get(response.data.drone_id);
        setDrone(droneRes.data);
      }
    } catch (error) {
      toast.error('Mission non trouvée');
      navigate('/missions');
    } finally {
      setLoading(false);
    }
  };

  const getRiskConfig = (level) => {
    const configs = {
      LOW: { 
        color: '#10b981', 
        bgClass: 'bg-emerald-50 border-emerald-300',
        textClass: 'text-emerald-700',
        label: 'FAIBLE',
        icon: CheckCircle
      },
      MODERATE: { 
        color: '#f59e0b', 
        bgClass: 'bg-amber-50 border-amber-300',
        textClass: 'text-amber-700',
        label: 'MODÉRÉ',
        icon: AlertTriangle
      },
      HIGH: { 
        color: '#ef4444', 
        bgClass: 'bg-red-50 border-red-300',
        textClass: 'text-red-700',
        label: 'ÉLEVÉ',
        icon: XCircle
      },
    };
    return configs[level] || configs.LOW;
  };

  const generatePDF = useCallback(async () => {
    if (!mission) return;
    
    setGenerating(true);
    toast.info('Génération du PDF en cours...');
    
    // Helper function to clean text for PDF (remove special chars, convert accents)
    const cleanText = (text) => {
      if (!text) return '';
      return text
        .replace(/[éèêë]/g, 'e')
        .replace(/[àâä]/g, 'a')
        .replace(/[ùûü]/g, 'u')
        .replace(/[îï]/g, 'i')
        .replace(/[ôö]/g, 'o')
        .replace(/[ç]/g, 'c')
        .replace(/[ÉÈÊË]/g, 'E')
        .replace(/[ÀÂÄÅ]/g, 'A')
        .replace(/[ÙÛÜ]/g, 'U')
        .replace(/[ÎÏ]/g, 'I')
        .replace(/[ÔÖ]/g, 'O')
        .replace(/[Ç]/g, 'C')
        .replace(/[œ]/g, 'oe')
        .replace(/[Œ]/g, 'OE')
        .replace(/[°]/g, ' deg')
        .replace(/[^\x00-\x7F]/g, ''); // Remove any remaining non-ASCII
    };
    
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const reg = mission.regulation_result;
      const riskConfig = getRiskConfig(reg.risk_level);
      const isFree = user?.subscription_status === 'free';
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 15;
      let yPos = 0;
      
      // Helper function to add new page if needed
      const checkNewPage = (requiredSpace) => {
        if (yPos + requiredSpace > pageHeight - 20) {
          doc.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // ===================== PAGE 1 - HEADER =====================
      // Header background
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, pageWidth, 45, 'F');
      
      // Logo and title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.text('Drone Maps', margin, 22);
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text('Rapport de Conformite Reglementaire Drone', margin, 32);
      doc.setFontSize(9);
      doc.text('Reglement UE 2019/947', margin, 39);
      
      // Mission ID and date
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`Mission ID: ${mission.id.slice(0, 8)}`, pageWidth - margin - 45, 22);
      doc.text(`Genere le: ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - margin - 45, 28);
      doc.text(`Version: ${reg.regulation_version}`, pageWidth - margin - 45, 34);
      
      // Watermark for free users
      if (isFree) {
        doc.setTextColor(220, 220, 220);
        doc.setFontSize(70);
        doc.setFont('helvetica', 'bold');
        doc.text('DEMO', 50, 180, { angle: 45 });
        doc.setFont('helvetica', 'normal');
      }
      
      yPos = 55;
      
      // ===================== OPERATOR & DRONE INFO =====================
      doc.setFillColor(248, 250, 252);
      doc.rect(margin, yPos, pageWidth - 2 * margin, 30, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.rect(margin, yPos, pageWidth - 2 * margin, 30, 'S');
      
      // Operator
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(8);
      doc.text('OPERATEUR', margin + 5, yPos + 7);
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(cleanText(user?.name || 'N/A'), margin + 5, yPos + 15);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(user?.email || 'N/A', margin + 5, yPos + 22);
      
      // Drone
      doc.setTextColor(100, 116, 139);
      doc.setFontSize(8);
      doc.text('DRONE', pageWidth / 2 + 5, yPos + 7);
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(cleanText(drone?.model_name || 'N/A'), pageWidth / 2 + 5, yPos + 15);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Classe ${drone?.ce_class || 'N/A'} - ${drone?.weight_kg || 'N/A'} kg`, pageWidth / 2 + 5, yPos + 22);
      
      yPos += 38;
      
      // ===================== MISSION DETAILS =====================
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Details de la Mission', margin, yPos);
      yPos += 8;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);
      
      const missionDetails = [
        [`Type: ${cleanText(mission.mission_type)}`, `Date: ${new Date(mission.mission_date).toLocaleDateString('fr-FR')}`],
        [`Heure: ${mission.mission_time}`, `Altitude: ${mission.altitude_requested}m`],
        [`Zone urbaine: ${reg.is_urban_zone ? 'Oui' : 'Non'}`, `Vol de nuit: ${mission.is_night_mission ? 'Oui' : 'Non'}`]
      ];
      
      // Add urban detection info if available
      if (mission.urban_detection_info && reg.is_urban_zone) {
        const confidence = mission.urban_detection_info.confidence === 'high' ? 'Haute' : 
                          mission.urban_detection_info.confidence === 'medium' ? 'Moyenne' : 'Basse';
        missionDetails.push([`Detection auto: ${mission.urban_detection_info.reason || 'N/A'}`, `Confiance: ${confidence}`]);
      }
      
      missionDetails.forEach(row => {
        doc.text(row[0], margin + 5, yPos);
        doc.text(row[1], pageWidth / 2 + 5, yPos);
        yPos += 6;
      });
      
      yPos += 5;
      
      // ===================== RISK SCORE & CATEGORY =====================
      // Risk score box
      const scoreColor = riskConfig.color;
      doc.setFillColor(parseInt(scoreColor.slice(1,3), 16), parseInt(scoreColor.slice(3,5), 16), parseInt(scoreColor.slice(5,7), 16));
      doc.roundedRect(margin, yPos, 50, 35, 3, 3, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.text('SCORE DE RISQUE', margin + 8, yPos + 10);
      doc.setFontSize(26);
      doc.setFont('helvetica', 'bold');
      doc.text(String(reg.risk_score), margin + 15, yPos + 27);
      doc.setFontSize(9);
      doc.text(riskConfig.label, margin + 13, yPos + 33);
      
      // Category info
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Categorie de Vol', margin + 60, yPos + 8);
      
      doc.setFontSize(16);
      const categoryColor = reg.category?.includes('OPEN') ? [16, 185, 129] : 
                           reg.category?.includes('SPECIFIC') ? [245, 158, 11] : [239, 68, 68];
      doc.setTextColor(...categoryColor);
      doc.text(cleanText(reg.sub_category || reg.category?.replace('_', ' ')), margin + 60, yPos + 18);
      
      doc.setTextColor(71, 85, 105);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      
      // Générer le résumé des autorisations basé sur authorities_to_contact
      let authSummary = [];
      if (reg.authorities_to_contact && reg.authorities_to_contact.length > 0) {
        reg.authorities_to_contact.forEach(auth => {
          if (auth.authority?.includes('Prefecture') || auth.authority?.includes('préfecture')) {
            if (!authSummary.includes('Prefecture')) authSummary.push('Prefecture');
          }
          if (auth.authority?.includes('aeroport') || auth.authority?.includes('Aeroport') || 
              auth.authority?.includes('CTR') || auth.authority?.includes('TWR')) {
            if (!authSummary.includes('Aeroport/ATC')) authSummary.push('Aeroport/ATC');
          }
          if (auth.authority?.includes('TMA') || auth.authority?.includes('CTA')) {
            if (!authSummary.includes('Controle approche')) authSummary.push('Controle approche');
          }
          if (auth.authority?.includes('DSAC') || auth.authority?.includes('SORA')) {
            if (!authSummary.includes('DSAC (SORA)')) authSummary.push('DSAC (SORA)');
          }
        });
      }
      
      // Déterminer le texte d'autorisation
      let authText = 'Aucune requise';
      if (reg.sora_required) {
        authText = 'SORA obligatoire (DSAC)';
      } else if (reg.authorization_type === 'sora') {
        authText = 'SORA obligatoire (DSAC)';
      } else if (reg.authorization_type === 'authorization') {
        authText = 'Autorisation DGAC requise';
      } else if (reg.authorization_type === 'declaration') {
        authText = 'Declaration requise';
      } else if (authSummary.length > 0) {
        authText = 'Voir autorisations ci-dessous';
      }
      
      // Ajouter le résumé des autorités si présent
      if (authSummary.length > 0) {
        authText = authSummary.join(', ');
      }
      
      doc.text(`Autorisation: ${authText}`, margin + 60, yPos + 26);
      doc.text(`Altitude max: ${reg.max_altitude_allowed}m AGL`, margin + 60, yPos + 32);
      
      // Afficher le délai si présent
      if (reg.authorization_delay) {
        doc.setTextColor(245, 158, 11);
        doc.setFontSize(8);
        doc.text(`Delai: ${reg.authorization_delay}`, margin + 60, yPos + 38);
        yPos += 8;
      }
      
      yPos += 45;
      
      // ===================== MAP CAPTURE =====================
      if (mapContainerRef.current) {
        try {
          const mapElement = mapContainerRef.current.querySelector('.leaflet-container');
          if (mapElement) {
            // Attendre que la carte soit stable et centrée
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Forcer un recentrage sur le polygone si l'instance de carte est disponible
            if (mapInstanceRef.current && mission.polygon_geometry) {
              try {
                const layer = window.L.geoJSON(mission.polygon_geometry);
                const bounds = layer.getBounds();
                if (bounds.isValid()) {
                  mapInstanceRef.current.fitBounds(bounds, { padding: [30, 30], animate: false });
                  // Attendre le recentrage et le chargement des tuiles
                  await new Promise(resolve => setTimeout(resolve, 800));
                }
              } catch (e) {
                console.warn('Could not recenter map:', e);
              }
            }
            
            const canvas = await html2canvas(mapElement, {
              useCORS: true,
              allowTaint: true,
              scale: 2,
              logging: false,
              backgroundColor: '#ffffff'
            });
            
            const imgData = canvas.toDataURL('image/jpeg', 0.8);
            const imgWidth = pageWidth - 2 * margin;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            const maxHeight = 70;
            const finalHeight = Math.min(imgHeight, maxHeight);
            
            doc.setTextColor(30, 41, 59);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text('Zone de Vol', margin, yPos);
            yPos += 5;
            
            doc.setDrawColor(226, 232, 240);
            doc.setLineWidth(0.5);
            doc.rect(margin, yPos, imgWidth, finalHeight, 'S');
            doc.addImage(imgData, 'JPEG', margin, yPos, imgWidth, finalHeight);
            yPos += finalHeight + 10;
          }
        } catch (e) {
          console.warn('Map capture failed:', e);
        }
      }
      
      // ===================== WARNINGS =====================
      if (reg.warnings && reg.warnings.length > 0) {
        checkNewPage(30);
        doc.setFillColor(254, 242, 242);
        doc.rect(margin, yPos, pageWidth - 2 * margin, 8 + reg.warnings.length * 6, 'F');
        doc.setDrawColor(252, 165, 165);
        doc.rect(margin, yPos, pageWidth - 2 * margin, 8 + reg.warnings.length * 6, 'S');
        
        doc.setTextColor(185, 28, 28);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('! ALERTES', margin + 5, yPos + 6);
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        reg.warnings.forEach((warning, idx) => {
          // Remove any special characters from warnings
          const cleanWarning = warning.replace(/[^\x00-\x7F]/g, '');
          doc.text(`- ${cleanWarning}`, margin + 8, yPos + 12 + idx * 6);
        });
        yPos += 15 + reg.warnings.length * 6;
      }
      
      // ===================== PAGE 2 - AUTHORITIES TO CONTACT =====================
      if (reg.authorities_to_contact && reg.authorities_to_contact.length > 0) {
        doc.addPage();
        yPos = margin;
        
        // Header for page 2
        doc.setFillColor(15, 23, 42);
        doc.rect(0, 0, pageWidth, 25, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Administrations a Contacter', margin, 16);
        
        yPos = 35;
        
        reg.authorities_to_contact.forEach((auth, idx) => {
          checkNewPage(40);
          
          // Contact type badge color
          const badgeColors = {
            'interdiction': [239, 68, 68],
            'autorisation': [245, 158, 11],
            'notification': [59, 130, 246],
            'information': [107, 114, 128]
          };
          const badgeColor = badgeColors[auth.contact_type] || badgeColors.information;
          
          // Card background
          doc.setFillColor(248, 250, 252);
          doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 35, 2, 2, 'F');
          
          // Left border accent
          doc.setFillColor(...badgeColor);
          doc.rect(margin, yPos, 3, 35, 'F');
          
          // Badge
          doc.setFillColor(...badgeColor);
          doc.roundedRect(margin + 8, yPos + 4, 30, 6, 1, 1, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(6);
          doc.setFont('helvetica', 'bold');
          doc.text(auth.contact_type.toUpperCase(), margin + 12, yPos + 8.5);
          
          // Authority name
          doc.setTextColor(30, 41, 59);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.text(cleanText(auth.authority), margin + 8, yPos + 18);
          
          // Reason
          doc.setTextColor(71, 85, 105);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          const reasonText = doc.splitTextToSize(cleanText(auth.reason), pageWidth - 2 * margin - 20);
          doc.text(reasonText[0], margin + 8, yPos + 25);
          
          // Frequency & contact info
          if (auth.frequency) {
            doc.setFillColor(224, 242, 254);
            doc.roundedRect(margin + 8, yPos + 28, 45, 5, 1, 1, 'F');
            doc.setTextColor(3, 105, 161);
            doc.setFontSize(7);
            doc.setFont('helvetica', 'bold');
            doc.text(`FREQ: ${auth.frequency} MHz`, margin + 11, yPos + 31.5);
          }
          
          // URL
          if (auth.url) {
            doc.setTextColor(14, 165, 233);
            doc.setFontSize(7);
            doc.text(auth.url, pageWidth - margin - 50, yPos + 31.5);
          }
          
          // Details
          if (auth.details) {
            doc.setTextColor(100, 116, 139);
            doc.setFontSize(7);
            doc.setFont('helvetica', 'italic');
            const detailsText = doc.splitTextToSize(cleanText(auth.details), pageWidth - 2 * margin - 20);
            doc.text(detailsText[0], margin + 8, yPos + 33);
          }
          
          yPos += 40;
        });
        
        // Tips box
        yPos += 5;
        checkNewPage(25);
        doc.setFillColor(224, 242, 254);
        doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 20, 2, 2, 'F');
        doc.setTextColor(3, 105, 161);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('CONSEILS', margin + 5, yPos + 7);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text('- Effectuez vos demarches au minimum 10 jours ouvres avant la date de vol', margin + 5, yPos + 13);
        doc.text('- Conservez tous les numeros de dossier et confirmations recues', margin + 5, yPos + 18);
      }
      
      // ===================== CHECKLIST PAGE =====================
      doc.addPage();
      yPos = margin;
      
      // Header
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, pageWidth, 25, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Checklist des Obligations', margin, 16);
      
      yPos = 35;
      
      // Checklist items
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      
      reg.checklist?.forEach((item, idx) => {
        checkNewPage(10);
        // Checkbox
        doc.setDrawColor(203, 213, 225);
        doc.setLineWidth(0.3);
        doc.rect(margin + 3, yPos - 3, 4, 4, 'S');
        
        // Text - clean the item text
        const itemText = doc.splitTextToSize(cleanText(item), pageWidth - 2 * margin - 15);
        doc.text(itemText, margin + 12, yPos);
        yPos += 5 + (itemText.length - 1) * 4;
      });
      
      // ===================== FOOTER ON ALL PAGES =====================
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        
        // Footer line
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.3);
        doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
        
        // Footer text
        doc.setTextColor(148, 163, 184);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text('Ce rapport est genere automatiquement par Drone Maps. Il ne se substitue pas aux autorites.', margin, pageHeight - 10);
        doc.text(`Page ${i}/${totalPages}`, pageWidth - margin - 15, pageHeight - 10);
        
        // Watermark on all pages for free users
        if (isFree && i > 1) {
          doc.setTextColor(230, 230, 230);
          doc.setFontSize(60);
          doc.setFont('helvetica', 'bold');
          doc.text('DEMO', 50, 150, { angle: 45 });
        }
      }
      
      // Save
      doc.save(`DroneMaps_Mission_${mission.id.slice(0, 8)}.pdf`);
      
      // Log the download
      try {
        await pdfApi.logDownload(mission.id, mission.mission_type);
      } catch (e) {
        console.warn('Failed to log PDF download:', e);
      }
      
      toast.success('PDF généré avec succès !');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setGenerating(false);
    }
  }, [mission, drone, user]);

  if (loading) {
    return (
      <DashboardLayout title="Détails Mission">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!mission) return null;

  const reg = mission.regulation_result;
  const riskConfig = getRiskConfig(reg.risk_level);
  const RiskIcon = riskConfig.icon;

  return (
    <DashboardLayout 
      title="Détails de la Mission"
      actions={
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={generatePDF}
            disabled={generating}
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {generating ? 'Génération...' : 'Télécharger PDF'}
          </Button>
        </div>
      }
    >
      <Link 
        to="/missions" 
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour aux missions
      </Link>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Risk Score Card */}
          <div className={`p-6 rounded-lg border-2 ${riskConfig.bgClass}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Score de Risque</p>
                <div className="flex items-center gap-4">
                  <span className={`text-5xl font-bold font-mono ${riskConfig.textClass}`}>
                    {reg.risk_score}
                  </span>
                  <div className="flex items-center gap-2">
                    <RiskIcon className={`w-6 h-6 ${riskConfig.textClass}`} />
                    <span className={`text-lg font-semibold ${riskConfig.textClass}`}>
                      {riskConfig.label}
                    </span>
                  </div>
                </div>
              </div>
              <div className={`
                w-16 h-16 rounded-full flex items-center justify-center
                ${reg.is_authorized ? 'bg-emerald-100' : 'bg-red-100'}
              `}>
                {reg.is_authorized ? (
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                ) : (
                  <XCircle className="w-8 h-8 text-red-600" />
                )}
              </div>
            </div>
          </div>

          {/* Warnings */}
          {reg.warnings && reg.warnings.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Alertes Réglementaires
              </h3>
              <ul className="space-y-2">
                {reg.warnings.map((warning, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-red-700">
                    <span className="text-red-500 mt-0.5">•</span>
                    {warning}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* NOUVEAU: Section Contraintes à Traiter (style Clearance) */}
          {reg.constraints_to_handle && reg.constraints_to_handle.length > 0 && (
            <div className="bg-white border-2 border-slate-300 rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-amber-500" />
                  CONTRAINTES À TRAITER
                </h3>
                <span className="px-4 py-2 text-lg font-bold bg-amber-500 text-white rounded-full">
                  {reg.constraints_count}
                </span>
              </div>
              
              <div className="space-y-4">
                {reg.constraints_to_handle.map((constraint, idx) => (
                  <ConstraintCard 
                    key={constraint.id || idx} 
                    constraint={constraint}
                    missionDate={mission.mission_date}
                  />
                ))}
              </div>
              
              {/* Message d'aide */}
              <div className="mt-6 pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-500 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Traitez toutes les contraintes avant la date de vol pour être en conformité.
                </p>
              </div>
            </div>
          )}

          {/* Zone Urbaine Auto-Detection Info */}
          {(reg.is_urban_zone || mission.urban_detection_info) && (
            <div className={`border rounded-lg p-4 ${
              reg.is_urban_zone 
                ? 'bg-amber-50 border-amber-200' 
                : 'bg-emerald-50 border-emerald-200'
            }`}>
              <h3 className={`font-semibold mb-3 flex items-center gap-2 ${
                reg.is_urban_zone ? 'text-amber-800' : 'text-emerald-800'
              }`}>
                <Building2 className="w-5 h-5" />
                {reg.is_urban_zone ? 'Zone Peuplée Détectée' : 'Zone Non Peuplée'}
              </h3>
              <div className="space-y-2 text-sm">
                {mission.urban_detection_info && (
                  <>
                    <div className={`flex items-center gap-2 ${
                      reg.is_urban_zone ? 'text-amber-700' : 'text-emerald-700'
                    }`}>
                      <span className="font-medium">Détection automatique:</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        mission.urban_detection_info.confidence === 'high' 
                          ? 'bg-amber-100 text-amber-800' 
                          : mission.urban_detection_info.confidence === 'medium'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-slate-100 text-slate-700'
                      }`}>
                        Confiance: {mission.urban_detection_info.confidence === 'high' ? 'Haute' : 
                                    mission.urban_detection_info.confidence === 'medium' ? 'Moyenne' : 'Basse'}
                      </span>
                    </div>
                    {mission.urban_detection_info.reason && (
                      <p className={reg.is_urban_zone ? 'text-amber-700' : 'text-emerald-700'}>
                        <span className="font-medium">Raison:</span> {mission.urban_detection_info.reason}
                      </p>
                    )}
                    {mission.urban_detection_info.place_name && (
                      <p className={reg.is_urban_zone ? 'text-amber-700' : 'text-emerald-700'}>
                        <span className="font-medium">Lieu:</span> {mission.urban_detection_info.place_name}
                      </p>
                    )}
                  </>
                )}
                {reg.is_urban_zone && (
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <p className="text-amber-800 font-medium">
                      ⚠️ En zone peuplée, les drones C3/C4 nécessitent une autorisation préfectorale (STS-01).
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section Héliports/Aéroports Proches (NOUVEAU) */}
          {mission.nearby_airports_info && mission.nearby_airports_info.total_count > 0 && (
            <NearbyAirportsCard nearbyAirportsInfo={mission.nearby_airports_info} />
          )}

          {/* Regulation Details */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2 font-[Barlow]">
              <Shield className="w-5 h-5 text-sky-500" />
              Analyse Réglementaire EU 2019/947
            </h3>
            
            {/* Category Badge */}
            <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Catégorie de vol</p>
                  <div className="flex items-center gap-2">
                    <span className={`
                      text-xl font-bold
                      ${reg.category?.includes('OPEN_A1') ? 'text-emerald-600' :
                        reg.category?.includes('OPEN_A2') ? 'text-sky-600' :
                        reg.category?.includes('OPEN_A3') ? 'text-blue-600' :
                        reg.category?.includes('STS') ? 'text-amber-600' :
                        reg.category?.includes('SORA') ? 'text-red-600' :
                        'text-slate-900'}
                    `}>
                      {reg.sub_category || reg.category?.replace('_', ' ')}
                    </span>
                    {reg.scenario && (
                      <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-medium">
                        {reg.scenario}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mt-1">
                    {reg.category?.includes('OPEN') ? '🟢 Catégorie Ouverte' : 
                     reg.category?.includes('SPECIFIC') ? '🟡 Catégorie Spécifique' :
                     '🔴 Non autorisé'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Type d'autorisation</p>
                  <span className={`
                    px-3 py-1 rounded-full text-sm font-semibold
                    ${reg.authorization_type === 'none' ? 'bg-emerald-100 text-emerald-700' :
                      reg.authorization_type === 'declaration' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'}
                  `}>
                    {reg.authorization_type === 'none' ? 'Aucune' :
                     reg.authorization_type === 'declaration' ? 'Déclaration' :
                     reg.authorization_type === 'interdiction' ? 'Interdit' :
                     'Autorisation DGAC'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Details Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-slate-500">Altitude max autorisée</p>
                <p className="font-semibold text-slate-900 font-mono">{reg.max_altitude_allowed}m AGL</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Superficie zone de vol</p>
                <p className="font-semibold text-slate-900 font-mono">
                  {formatArea(calculatePolygonArea(mission.polygon_geometry))}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Distance min personnes</p>
                <p className="font-semibold text-slate-900 font-mono">
                  {reg.min_distance_persons || 0}m
                  {reg.category === 'OPEN_A2' && <span className="text-slate-500 text-xs ml-1">(5m mode lent)</span>}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Survol personnes</p>
                <span className={`
                  inline-flex items-center px-2 py-1 rounded text-xs font-medium
                  ${reg.can_overfly_persons ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}
                `}>
                  {reg.can_overfly_persons ? 'AUTORISÉ' : 'INTERDIT'}
                </span>
              </div>
              <div>
                <p className="text-sm text-slate-500">Vol en zone urbaine</p>
                <span className={`
                  inline-flex items-center px-2 py-1 rounded text-xs font-medium
                  ${reg.can_fly_urban ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}
                `}>
                  {reg.can_fly_urban ? 'AUTORISÉ' : 'INTERDIT'}
                </span>
              </div>
              <div>
                <p className="text-sm text-slate-500">Vol à vue (VLOS)</p>
                <span className={`
                  inline-flex items-center px-2 py-1 rounded text-xs font-medium
                  ${reg.vlos_required ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}
                `}>
                  {reg.vlos_required ? 'OBLIGATOIRE' : 'BVLOS autorisé'}
                </span>
              </div>
              <div>
                <p className="text-sm text-slate-500">Version réglementation</p>
                <p className="font-mono text-sm text-slate-700">{reg.regulation_version}</p>
              </div>
            </div>

            {/* Restrictions */}
            {reg.restrictions && reg.restrictions.length > 0 && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-sm text-slate-500 mb-3 font-semibold">Restrictions applicables</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {reg.restrictions.slice(0, 8).map((restriction, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-slate-400 mt-0.5">•</span>
                      {restriction}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Intersected Zones */}
            {reg.intersected_zones && reg.intersected_zones.length > 0 && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-sm text-slate-500 mb-3">Zones intersectées</p>
                <div className="flex flex-wrap gap-2">
                  {reg.intersected_zones.map((zone, idx) => (
                    <span 
                      key={idx}
                      className={`
                        px-2 py-1 rounded text-xs font-medium
                        ${zone.type === 'P' ? 'bg-red-100 text-red-700' :
                          zone.type === 'R' ? 'bg-amber-100 text-amber-700' :
                          zone.type === 'CTR' ? 'bg-sky-100 text-sky-700' :
                          'bg-slate-100 text-slate-700'}
                      `}
                    >
                      {zone.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* ED-269 Official UAS Zones (EU 2019/947 Article 15) */}
            {reg.ed269_intersections && reg.ed269_intersections.length > 0 && (
              <div className="mt-6 pt-6 border-t border-slate-200" data-testid="ed269-section">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      ⭐ Zones UAS officielles intersectées
                      <span className="text-xs font-normal text-slate-500">(ED-269 · EU 2019/947 Art. 15)</span>
                    </p>
                    {reg.ed269_critical_count > 0 && (
                      <p className="text-xs text-red-700 mt-1 font-medium">
                        🚨 {reg.ed269_critical_count} zone{reg.ed269_critical_count > 1 ? 's' : ''} critique{reg.ed269_critical_count > 1 ? 's' : ''} (PROHIBITED ou autorisation requise)
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded">
                    {reg.ed269_intersections.length} zone{reg.ed269_intersections.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="space-y-2">
                  {reg.ed269_intersections.slice(0, 10).map((z, idx) => {
                    const typeColor = {
                      PROHIBITED: 'bg-red-50 border-red-200 text-red-900',
                      REQ_AUTHORISATION: 'bg-orange-50 border-orange-200 text-orange-900',
                      RESTRICTED: 'bg-orange-50 border-orange-200 text-orange-900',
                      CONDITIONAL: 'bg-amber-50 border-amber-200 text-amber-900',
                      NO_RESTRICTION: 'bg-green-50 border-green-200 text-green-900',
                    }[z.type] || 'bg-slate-50 border-slate-200 text-slate-900';
                    const badgeColor = {
                      PROHIBITED: 'bg-red-600 text-white',
                      REQ_AUTHORISATION: 'bg-orange-500 text-white',
                      RESTRICTED: 'bg-orange-500 text-white',
                      CONDITIONAL: 'bg-amber-500 text-white',
                      NO_RESTRICTION: 'bg-green-500 text-white',
                    }[z.type] || 'bg-slate-500 text-white';
                    return (
                      <div key={idx} className={`border rounded-lg p-3 ${typeColor}`} data-testid={`ed269-zone-${idx}`}>
                        <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
                          <div className="font-medium text-sm">{z.name}</div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${badgeColor}`}>{z.type}</span>
                            <span className="text-[10px] text-slate-600">{Math.round((z.coverage_ratio || 0) * 100)}% recouvrement</span>
                          </div>
                        </div>
                        {z.message && (
                          <p className="text-xs text-slate-700 mb-1">{z.message}</p>
                        )}
                        <div className="flex flex-wrap gap-3 text-[11px] text-slate-600">
                          {z.altitude && (z.altitude.lowerLimit !== null || z.altitude.upperLimit !== null) && (
                            <span>📏 Alt : {z.altitude.lowerLimit ?? '?'}{z.altitude.uom || 'm'} → {z.altitude.upperLimit ?? '?'}{z.altitude.uom || 'm'}</span>
                          )}
                          {z.reason && z.reason.length > 0 && (
                            <span>🏷️ {z.reason.join(', ')}</span>
                          )}
                          {z.authority && z.authority.name && (
                            <span>🏛️ {z.authority.name}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {reg.ed269_intersections.length > 10 && (
                    <p className="text-xs text-slate-500 italic text-center">
                      … et {reg.ed269_intersections.length - 10} zone{reg.ed269_intersections.length - 10 > 1 ? 's' : ''} supplémentaire{reg.ed269_intersections.length - 10 > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Checklist */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2 font-[Barlow]">
              <FileText className="w-5 h-5 text-sky-500" />
              Checklist Obligations
            </h3>
            <ul className="space-y-3">
              {reg.checklist?.map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded border border-slate-300 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Authorities to Contact */}
          {reg.authorities_to_contact && reg.authorities_to_contact.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="authorities-section">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2 font-[Barlow]">
                <Building2 className="w-5 h-5 text-sky-500" />
                Administrations à Contacter
              </h3>
              <div className="space-y-4">
                {reg.authorities_to_contact.map((auth, idx) => (
                  <div 
                    key={idx} 
                    className={`
                      p-4 rounded-lg border-l-4
                      ${auth.contact_type === 'interdiction' ? 'bg-red-50 border-red-500' :
                        auth.contact_type === 'autorisation' ? 'bg-amber-50 border-amber-500' :
                        auth.contact_type === 'notification' ? 'bg-blue-50 border-blue-500' :
                        'bg-slate-50 border-slate-400'}
                    `}
                    data-testid={`authority-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`
                            text-xs font-medium px-2 py-0.5 rounded uppercase
                            ${auth.contact_type === 'interdiction' ? 'bg-red-200 text-red-800' :
                              auth.contact_type === 'autorisation' ? 'bg-amber-200 text-amber-800' :
                              auth.contact_type === 'notification' ? 'bg-blue-200 text-blue-800' :
                              'bg-slate-200 text-slate-800'}
                          `}>
                            {auth.contact_type}
                          </span>
                        </div>
                        <h4 className="font-semibold text-slate-900 mb-1">{auth.authority}</h4>
                        <p className="text-sm text-slate-600 mb-2">{auth.reason}</p>
                        
                        {/* Frequency info for controlled zones */}
                        {auth.frequency && (
                          <div className="flex items-center gap-4 mb-2 p-2 bg-white/50 rounded border border-slate-200">
                            <div className="flex items-center gap-1.5 text-sm">
                              <Radio className="w-4 h-4 text-sky-600" />
                              <span className="font-mono font-bold text-sky-700">{auth.frequency} MHz</span>
                            </div>
                            {auth.service_name && (
                              <div className="flex items-center gap-1.5 text-sm text-slate-600">
                                <Phone className="w-4 h-4" />
                                <span>{auth.service_name}</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {auth.details && (
                          <p className="text-xs text-slate-500 italic">{auth.details}</p>
                        )}
                      </div>
                      {auth.url && (
                        <a 
                          href={auth.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-sky-600 hover:text-sky-700 hover:underline whitespace-nowrap"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Plus d'infos
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Helper notice */}
              <div className="mt-4 p-3 bg-sky-50 border border-sky-200 rounded-lg">
                <p className="text-xs text-sky-700">
                  <strong>Conseil :</strong> Effectuez vos démarches au minimum 10 jours ouvrés avant la date de vol prévue. 
                  Conservez tous les numéros de dossier et confirmations reçues.
                </p>
              </div>
            </div>
          )}

          {/* Map */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" ref={mapContainerRef}>
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2 font-[Barlow]">
                <MapPin className="w-5 h-5 text-sky-500" />
                Zone de Vol
              </h3>
            </div>
            <div style={{ height: '550px' }}>
              <FlightMap
                initialPolygon={mission.polygon_geometry}
                showZones={true}
                editable={false}
                compactMode={true}
                height="100%"
                onMapReady={handleMapReady}
                userCountry={user?.country}
              />
            </div>
          </div>

          {/* NOTAM Panel */}
          {mission.polygon_geometry && (
            <NotamPanel 
              lat={
                mission.polygon_geometry.coordinates 
                  ? mission.polygon_geometry.coordinates[0][0][1]  // GeoJSON format
                  : (mission.polygon_geometry[0] ? mission.polygon_geometry[0][1] : null)  // Array format
              } 
              lng={
                mission.polygon_geometry.coordinates 
                  ? mission.polygon_geometry.coordinates[0][0][0]  // GeoJSON format
                  : (mission.polygon_geometry[0] ? mission.polygon_geometry[0][0] : null)  // Array format
              } 
            />
          )}

          {/* Weather briefing — replays the snapshot stored at creation
              time but also refreshes live data so the pilot can re-check
              conditions just before take-off. */}
          {mission.polygon_geometry && (() => {
            const c = polygonCentroid(mission.polygon_geometry);
            if (!c) return null;
            return (
              <WeatherPanel
                lat={c.lat}
                lon={c.lon}
                date={mission.mission_date}
                time={mission.mission_time}
                ceClass={drone?.ce_class}
                droneName={drone?.model_name}
                snapshot={mission.weather_snapshot}
              />
            );
          })()}
        </div>

        {/* Right Column - Summary */}
        <div className="space-y-6">
          {/* Mission Info */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h3 className="font-semibold text-slate-900 mb-4 font-[Barlow]">Informations Mission</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Date</p>
                  <p className="font-medium text-slate-900">
                    {new Date(mission.mission_date).toLocaleDateString('fr-FR', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Heure</p>
                  <p className="font-medium text-slate-900">{mission.mission_time}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Gauge className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Altitude demandée</p>
                  <p className="font-medium text-slate-900 font-mono">{mission.altitude_requested}m</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Type</p>
                  <p className="font-medium text-slate-900 capitalize">{mission.mission_type}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Drone Info */}
          {drone && (
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h3 className="font-semibold text-slate-900 mb-4 font-[Barlow]">Drone Utilisé</h3>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center">
                  <Plane className="w-6 h-6 text-sky-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{drone.model_name}</p>
                  <p className="text-sm text-slate-600">
                    Classe {drone.ce_class} • {drone.weight_kg} kg
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Mission ID */}
          <div className="bg-slate-100 border border-slate-200 rounded-lg p-4">
            <p className="text-xs text-slate-500 mb-1">ID Mission</p>
            <p className="font-mono text-xs text-slate-700 break-all">{mission.id}</p>
            <p className="text-xs text-slate-500 mt-2">
              Créée le {new Date(mission.created_at).toLocaleString('fr-FR')}
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
